/**
 * LiveTurnStepsRail — the steps of a running turn, newest first, rotating as they
 * arrive, with a way to see all of them (§12 PR-F2 of
 * docs/PLAN-mid-turn-fold-in-20260915.md).
 *
 * Miles, 2026-09-21, from a screenshot of his own group turn: "each task should be
 * more detailed in what it does instead of just a canned message… also there was
 * actually 47 steps and not a hand full it was showing and there needs to be a way
 * to display this like a rotation transition etc. to go through 47 steps."
 *
 * So: the newest three rows at rest, each arriving with a short slide-and-fade
 * (honoured only where motion is welcome — PENDING_WIDGET_CSS's reduced-motion
 * block turns the transition off), a counter that says how many steps the run has
 * ACTUALLY taken, and a disclosure that opens every step still in the window.
 * `+N earlier` names what the server's window evicted rather than losing it
 * silently.
 *
 * REACT, not an html string: a disclosure that opens forty-seven rows cannot live
 * in a string the clock repaints every second, and the rows are keyed by their
 * ordinal so React moves them instead of rebuilding the list (which is what makes
 * the transition read as a rotation rather than a flicker).
 *
 * What it never does: light a processing flag, take a streaming lease, dispatch
 * anything, or show a stop control. It draws what the server already wrote.
 */
import React, { useState } from 'react';
import {
  LIVE_TURN_RAIL_VISIBLE_MAX,
  liveTurnRailView,
  type LiveTurnCard,
  type LiveTurnStepRow,
} from '../../services/groupThreads/liveTurns';

export interface LiveTurnStepsRailProps {
  card: Pick<LiveTurnCard, 'steps' | 'stepCount' | 'stepsDropped' | 'taskId'>;
  /** Rows on screen at rest, before the disclosure. */
  visibleMax?: number;
}

/** The glyph a step wears: still running, finished, or refused/threw. */
function stepGlyph(state: LiveTurnStepRow['state']): { glyph: string; className: string } {
  if (state === 'failed') return { glyph: '✕', className: 'is-failed' };
  if (state === 'done') return { glyph: '✓', className: 'is-done' };
  return { glyph: '●', className: 'is-running' };
}

function StepRow({ step }: { step: LiveTurnStepRow }): JSX.Element {
  const { glyph, className } = stepGlyph(step.state);
  return (
    <span className={`pending-live-turn-step ${className}`}>
      <span className="pending-live-turn-step-glyph" aria-hidden="true">{glyph}</span>
      <span className="pending-live-turn-step-label">{step.label}</span>
    </span>
  );
}

export function LiveTurnStepsRail({ card, visibleMax }: LiveTurnStepsRailProps): JSX.Element | null {
  const [open, setOpen] = useState(false);
  const view = liveTurnRailView(card, visibleMax ?? LIVE_TURN_RAIL_VISIBLE_MAX);
  if (!view.all.length) return null;
  const rows = open ? view.all : view.visible;
  return (
    <div className={`pending-live-turn-steps${open ? ' is-open' : ''}`} data-live-turn-steps={String(view.stepCount)}>
      {rows.map((s, i) => (
        // Keyed by ORDINAL so a new step pushes the list instead of rebuilding it —
        // that is what makes the arrival read as a rotation. The index fallback
        // covers an older server whose rows carry no ordinal.
        <StepRow key={s.n > 0 ? `n${s.n}` : `i${i}-${s.atMs}`} step={s} />
      ))}
      {view.dropped > 0 && open && (
        <span className="pending-live-turn-step is-earlier">
          <span className="pending-live-turn-step-glyph" aria-hidden="true">{'⋯'}</span>
          <span className="pending-live-turn-step-label">{`+${view.dropped} earlier`}</span>
        </span>
      )}
      {(view.hasMore || view.dropped > 0) && (
        <button
          type="button"
          className="pending-live-turn-steps-toggle"
          aria-expanded={open}
          onClick={(e) => {
            // The lane's own click handler looks for the own pill's stop button by
            // id; this control carries none and must not reach it either way.
            e.preventDefault();
            e.stopPropagation();
            setOpen((v) => !v);
          }}
        >
          {open ? 'show fewer steps' : `show all ${view.stepCount} steps`}
        </button>
      )}
    </div>
  );
}
